/** Date utilities used by Calendar and DatePicker.
 *
 *  All dates are treated as local-time — the component renders a
 *  calendar of "days" as labels, not instants. Consumers who need
 *  timezone-aware logic should convert before/after. */

export interface PlainDate {
  year: number;
  /** 0-indexed. 0 = January. */
  month: number;
  /** 1-indexed day-of-month. */
  day: number;
}

export function fromDate(d: Date): PlainDate {
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
}

export function toDate(pd: PlainDate): Date {
  return new Date(pd.year, pd.month, pd.day);
}

export function isSameDay(a: PlainDate, b: PlainDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

export function compareDay(a: PlainDate, b: PlainDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

export function addDays(pd: PlainDate, days: number): PlainDate {
  const d = toDate(pd);
  d.setDate(d.getDate() + days);
  return fromDate(d);
}

export function addMonths(pd: PlainDate, months: number): PlainDate {
  const d = new Date(pd.year, pd.month + months, 1);
  const year = d.getFullYear();
  const month = d.getMonth();
  /* Clamp the day to the last day of the new month if needed. */
  const lastDay = new Date(year, month + 1, 0).getDate();
  return { year, month, day: Math.min(pd.day, lastDay) };
}

export function startOfMonth(pd: PlainDate): PlainDate {
  return { year: pd.year, month: pd.month, day: 1 };
}

/** Number of days in `pd.year / pd.month`. */
export function daysInMonth(pd: PlainDate): number {
  return new Date(pd.year, pd.month + 1, 0).getDate();
}

/** Day-of-week of `pd` (0 = Sunday). */
export function dayOfWeek(pd: PlainDate): number {
  return toDate(pd).getDay();
}

export interface CalendarGrid {
  weeks: ReadonlyArray<ReadonlyArray<PlainDate>>;
}

/** Build a 6-row × 7-col grid of PlainDate, aligned to `weekStartsOn`.
 *  Leading/trailing days from adjacent months fill the grid so it's
 *  always 42 cells. */
export function buildCalendarGrid(monthAnchor: PlainDate, weekStartsOn: number): CalendarGrid {
  const first = startOfMonth(monthAnchor);
  const firstDow = dayOfWeek(first);
  /* Negative offset → days to subtract to reach the grid's first cell. */
  const offset = (firstDow - weekStartsOn + 7) % 7;
  const gridStart = addDays(first, -offset);
  const weeks: PlainDate[][] = [];
  for (let w = 0; w < 6; w++) {
    const row: PlainDate[] = [];
    for (let d = 0; d < 7; d++) {
      row.push(addDays(gridStart, w * 7 + d));
    }
    weeks.push(row);
  }
  return { weeks };
}

export function todayPlain(): PlainDate {
  return fromDate(new Date());
}

export function formatMonth(pd: PlainDate, locale: string): string {
  return toDate(pd).toLocaleDateString(locale, { year: 'numeric', month: 'long' });
}

export function formatDate(pd: PlainDate, locale: string): string {
  return toDate(pd).toLocaleDateString(locale);
}

/** Weekday short labels for the column header, starting at `weekStartsOn`. */
export function weekdayLabels(locale: string, weekStartsOn: number): ReadonlyArray<string> {
  const out: string[] = [];
  /* Arbitrary reference Sunday 2023-01-01; add `weekStartsOn + i` to
   * rotate. */
  const anchor = new Date(2023, 0, 1);
  for (let i = 0; i < 7; i++) {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + ((weekStartsOn + i) % 7));
    out.push(d.toLocaleDateString(locale, { weekday: 'short' }));
  }
  return out;
}
